import React, { act, useEffect, useMemo, useState } from "react";
import "./side-bar.scss";
import CloseIcon from "@mui/icons-material/Close";
import { SidebarProps } from "../types/side-bar";
import hamburgerIcon from "../../../assets/icons/hamburger.svg";
import { Nav, NavItem, NavLink as StrapLink } from "reactstrap";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { MenuItem } from "../types/menu-item";
import { logout } from "../../../common/routines/user-operations";
import { routes } from "../../../constants/routes";
import LogoutConfirmationPopup from "./logout-confirmation-popup/logout-confirmation-popup";
import DisplayVersion from "../../display-version/display-version";
import { AlertComponent } from "../../../common/components";
import ProfileSidebar from "../../profile/profile-sidebar";
import SubMenuSideBar from "./sub-menu/sub-menu";
import { Badge, Box, Tooltip } from "@mui/material";
import { useLogin } from "../../../common/hooks/useUser";
import avatorIcon from "../../../assets/icons/avatar.svg";
import { useSelector } from "react-redux";

interface AlertMessage {
  open: boolean;
  message?: any;
  type?: "success" | "error" | "info" | "warning";
  onClose?: () => void;
}

const igonreDefaultRoutes = ["Create Campaigns"];

const Sidebar: React.FC<SidebarProps> = ({ menuItems, footerItems }) => {
  const [activeNavbar, setActiveNavbar] = useState<{
    isMenuOpen: boolean;
    subMenuList: any[];
    activeMenu: string;
    activePath: string;
  }>({
    isMenuOpen: false,
    subMenuList: [],
    activeMenu: menuItems?.[0]?.label,
    activePath: routes.DASHBOARD,
  });
  const location = useLocation();
  const [clcikedActiveItem, setClickedActiveItem] = useState<{
    isMenuOpen: boolean;
    subMenuList: any[];
    activeMenu: string;
    activePath: string;
  }>({
    isMenuOpen: false,
    subMenuList: [],
    activeMenu: menuItems?.[0]?.label,
    activePath: routes.DASHBOARD,
  }); // default active
  const [hoverItem, setHoverItem] = useState<any>({}); // temp hover state
  const [isLogoutPopupOpen, setIsLogoutPopupOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<{
    isOpen: boolean;
    activePath: string;
  }>({ isOpen: false, activePath: "" });
  const navigate = useNavigate();
  const [alertMessage, setAlertMessage] = useState<AlertMessage>({
    open: false,
    message: "",
    type: "success",
  });
  const [isProfileSidebarOpen, setIsProfileSidebarOpen] = useState(false);
  const [isSidebarClick, setIsSidebarClick] = useState(false);
  const tenantDetails = useLogin();
  const profileUrl = useSelector(
    (state: any) => state?.session?.userDetails?.profile_url,
  );
  const isSuperParter = useMemo(() => {
    return tenantDetails?.tenantDetails?.tenantType === "SuperTenant";
  }, [tenantDetails?.tenantDetails?.tenantType]);

  const handleLogout = (): void => {
    setAlertMessage({
      open: true,
      message: "You have successfully logged out!",
      type: "success",
    });
    setTimeout(() => {
      logout();
      navigate(routes.LOGIN);
    }, 500);
  };

  const toggleCollapse = (label: string) => {
    if (collapsed?.isOpen && label === collapsed?.activePath) {
      setCollapsed({ isOpen: false, activePath: "" });
      return;
    }
    setCollapsed({ isOpen: true, activePath: label });
  };

  const handleHamburger = () => {
    setActiveNavbar((prev: any) => ({
      ...prev,
      isMenuOpen: !prev?.isMenuOpen,
    }));
    setClickedActiveItem((prev: any) => ({
      ...prev,
      isMenuOpen: !prev?.isMenuOpen,
    }));
  };

  const handleDefultNav = (submenu: any) => {
    for (const item of submenu) {
      if (item.path && !igonreDefaultRoutes.includes(item.label)) {
        navigate(item.path);
        return true;
      }
      if (item.subMenu && item.subMenu.length > 0) {
        const found = handleDefultNav(item.subMenu);
        if (found) {
          setCollapsed({
            isOpen: true,
            activePath: item.label,
          });
          return true;
        }
      }
    }
  };
  const handleMenuClick = (menu: any, isPageRefresh?: boolean) => {
    setTimeout(() => {
      setIsSidebarClick(false);
    }, 400);
    if (
      activeNavbar?.activeMenu === menu.label &&
      clcikedActiveItem?.activeMenu === menu.label
    ) {
      if (activeNavbar?.subMenuList?.length > 0) {
        setActiveNavbar((prev: any) => ({
          ...prev,
          isMenuOpen: !prev?.isMenuOpen,
        }));
        setClickedActiveItem((prev: any) => ({
          ...prev,
          isMenuOpen: !prev?.isMenuOpen,
        }));
      }
      return;
    } else {
      const hasSubMenu = menu?.subMenu?.length > 0;
      const hasPath = !!menu?.path;
      if (isProfileSidebarOpen) {
        setIsProfileSidebarOpen(false);
      }
      if (menu.label === "Logout") {
        setIsLogoutPopupOpen(true);
        return;
      }
      if (menu.label === "Profile" && !isSuperParter) {
        setIsProfileSidebarOpen(true);
        return;
      } else if (menu.label === "Profile") {
        navigate(routes.PROFILE);
        setIsProfileSidebarOpen(false);
      }

      if (hasSubMenu || hasPath) {
        setClickedActiveItem((prev: any) => ({
          ...prev,
          isMenuOpen: true,
          subMenuList: menu?.subMenu || [],
          activeMenu: menu?.label,
        }));
        setActiveNavbar((prev: any) => ({
          ...prev,
          isMenuOpen: true,
          subMenuList: menu?.subMenu || [],
          activeMenu: menu?.label,
        }));
        if (hasSubMenu) {
          if (!isPageRefresh) handleDefultNav(menu?.subMenu || []);
          handleFindActiveMenuItem(menu?.subMenu || [], location.pathname);
        }

        if (!hasSubMenu && hasPath) {
          toggleCollapse("");
          navigate(menu.path);
        }
        return;
      }

      const fallbackPath = menu.path || routes.DASHBOARD;
      setActiveNavbar({
        isMenuOpen: false,
        subMenuList: [],
        activeMenu: menuItems?.[0]?.label,
        activePath: fallbackPath,
      });
      setClickedActiveItem({
        isMenuOpen: false,
        subMenuList: [],
        activeMenu: menuItems?.[0]?.label,
        activePath: fallbackPath,
      });
      navigate(fallbackPath);
    }
  };

  const handleFindActiveMenuItem = (subMenu: any, path: string) => {
    for (const item of subMenu) {
      if (item.path && path.startsWith(item.path)) {
        return true;
      }
      if (item.subMenu && item.subMenu.length > 0) {
        const found = handleFindActiveMenuItem(item.subMenu, path);
        if (found) {
          setCollapsed({
            isOpen: true,
            activePath: item.label,
          });
          return true;
        }
      }
    }
    setCollapsed({
      isOpen: false,
      activePath: "",
    });
    return false;
  };

  const findActiveMenu = (sidebarNavs: any, path: string) => {
    return sidebarNavs.find((menu: any) => {
      if (menu.path && path.startsWith(menu.path)) {
        return true;
      } else if (menu.subMenu && menu.subMenu.length > 0) {
        return handleFindActiveMenuItem(menu.subMenu, path);
      }
      return false;
    });
  };

  const handleProfileClick = () => {
    const settings: any = findActiveMenu(footerItems, routes.PERSONAL_SETTINGS);
    if (settings) {
      setIsSidebarClick(true);
      handleMenuClick(settings);
    }
    navigate(routes.PERSONAL_SETTINGS);
    setIsProfileSidebarOpen(false);
  };

  useEffect(() => {
    const sidebarNavs = [...menuItems, ...footerItems];
    const activeMenuItem = findActiveMenu(sidebarNavs, location.pathname);
    if (
      activeMenuItem &&
      !isSidebarClick &&
      localStorage.getItem("previousPath") !==
        "/home/campaigns/create-campaign" &&
      localStorage.getItem("previousPath") !== "/home/campaigns/landing-page" &&
      localStorage.getItem("previousPath") !== "/home/surveys/create-survey" &&
      activeMenuItem?.label !== clcikedActiveItem?.activeMenu
    ) {
      if (location.pathname === routes.PROFILE) return;
      handleMenuClick(activeMenuItem, true);
    }
    localStorage.setItem("previousPath", location.pathname);
  }, [location]);

  const handleIfNoPath = (path: string, from?: string) => {
    setTimeout(() => {
      setIsSidebarClick(false);
    }, 400);
    if (!path) {
      setActiveNavbar({
        isMenuOpen: false,
        subMenuList: [],
        activeMenu: menuItems?.[0]?.label,
        activePath: routes.DASHBOARD,
      });
      setClickedActiveItem({
        isMenuOpen: false,
        subMenuList: [],
        activeMenu: menuItems?.[0]?.label,
        activePath: routes.DASHBOARD,
      });
    }
    if (!from)
      setCollapsed({
        isOpen: false,
        activePath: "",
      });
  };

  const handleMobileHamburgerClick = () => {
    const sidebarContainer = document.getElementById("sidebar-container");
    if (sidebarContainer) {
      sidebarContainer.classList.toggle("mobile-view");
    }
  };

  const modal: any = document.getElementById("sidebar-container");

  window.onclick = function (event) {
    if (event.target == modal) {
      modal.classList.add("mobile-view");
    }
  };

  return (
    <>
      <div
        className={`sidebar-hamburger-container ${
          tenantDetails?.gracePeriod
            ? "grace-period-alert-sidebar-container"
            : ""
        }`}
        id="sidebar-hamburger-container"
      >
        <div className="sidebar-hamburger" onClick={handleMobileHamburgerClick}>
          <img src={hamburgerIcon} className="hamburgerIcon" alt="menu" />
        </div>
      </div>
      <div
        className="sidebar-container"
        id="sidebar-container"
        onMouseLeave={() => {
          if (activeNavbar.activeMenu !== clcikedActiveItem.activeMenu) {
            setActiveNavbar({ ...clcikedActiveItem });
          }
        }}
      >
        <div className="sidebar">
          {/* Sidebar Menu */}
          <div className="sidebar-menu-items">
            <div className="hamburger-icon" onClick={handleHamburger}>
              <img src={hamburgerIcon} className="hamburgerIcon" alt="menu" />
            </div>
            {menuItems.map((item: any, index: number) => (
              <Tooltip
                key={index}
                title={item.label || ""}
                placement="right"
                sx={{ cursor: "pointer" }}
                disableInteractive
                PopperProps={{
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -15], // [skid, distance]
                      },
                    },
                  ],
                }}
                arrow
              >
                <button
                  key={item.label}
                  className={`nav-button menu-button ${activeNavbar.activeMenu === item.label ? "selected" : ""}`}
                  onClick={() => {
                    setIsSidebarClick(true);
                    handleMenuClick(item);
                  }}
                  onMouseEnter={() => {
                    if (item.label !== activeNavbar.activeMenu) {
                      setActiveNavbar((prev: any) => ({
                        ...prev,
                        isMenuOpen: true,
                        subMenuList: item?.subMenu || [],
                        activeMenu: item?.label,
                      }));
                    }
                  }}
                  onMouseLeave={() => {
                    if (activeNavbar.activeMenu !== item.label) {
                      setActiveNavbar({ ...clcikedActiveItem });
                    }
                  }}
                >
                  <b></b>
                  <b></b>

                  <img src={item.icon} alt={item.label} className="menu-icon" />
                </button>
              </Tooltip>
            ))}
          </div>

          <div className="sidebar-footer">
            {footerItems
              .map((item) => (
                <Tooltip
                  key={item.label}
                  title={item.label || ""}
                  placement="right"
                  disableInteractive
                  PopperProps={{
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [0, -15],
                        },
                      },
                    ],
                  }}
                  arrow
                >
                  <NavItem key={item.label} className="footer-item">
                    <StrapLink
                      className={`nav-button footer-button ${activeNavbar.activeMenu === item.label ? "selected" : ""}`}
                      onClick={() => {
                        setIsSidebarClick(true);
                        handleMenuClick(item);
                      }}
                      onMouseEnter={() => {
                        if (item.label !== activeNavbar.activeMenu) {
                          setActiveNavbar((prev: any) => ({
                            ...prev,
                            isMenuOpen: true,
                            subMenuList: item?.subMenu || [],
                            activeMenu: item?.label,
                          }));
                        }
                      }}
                      onMouseLeave={() => {
                        if (
                          activeNavbar.activeMenu !== item.label ||
                          item.label !== "Settings"
                        ) {
                          setActiveNavbar({ ...clcikedActiveItem });
                        }
                      }}
                    >
                      <b></b>
                      <b></b>
                      <Badge
                        badgeContent={" "}
                        overlap="circular"
                        color="error"
                        variant="dot"
                        invisible={!(item.label === "Notifications")}
                      >
                        <img
                          src={item.icon}
                          alt={item.label}
                          className="footer-icon"
                        />
                      </Badge>
                    </StrapLink>
                  </NavItem>
                </Tooltip>
              ))}
          </div>
        </div>

        {/* Submenu */}
        {activeNavbar?.subMenuList?.length > 0 && activeNavbar?.isMenuOpen && (
          <Box
            sx={{
              transform: activeNavbar?.isMenuOpen
                ? "translateX(0)"
                : "translateX(-100%)",
              transition: "transform 0.9s ease-in-out",
            }}
          >
            <div className="submenu-container open">
              <div className="menu-content">
                <div className="submenu-header">
                  <h2>{activeNavbar?.activeMenu}</h2>
                  <CloseIcon
                    fontSize="small"
                    sx={{ width: 16, height: 16, cursor: "pointer" }}
                    onClick={handleHamburger}
                  />
                </div>
                {activeNavbar.subMenuList?.length ? (
                  <Nav className="submenu-nav">
                    {activeNavbar.subMenuList.map((sub: any) => (
                      <>
                        {sub?.subMenu?.length ? (
                          <SubMenuSideBar
                            navSubMenu={sub}
                            collapsed={collapsed}
                            toggleCollapse={toggleCollapse}
                            handleIfNoPath={handleIfNoPath}
                            setIsSidebarClick={setIsSidebarClick}
                            activeNavbar={activeNavbar}
                            setClickedActiveItem={setClickedActiveItem}
                          />
                        ) : (
                          <NavItem key={sub.label}>
                            <NavLink
                              to={sub.path || routes.DASHBOARD}
                              onClick={() => {
                                setIsSidebarClick(true);
                                handleIfNoPath(sub.path);
                                setClickedActiveItem({
                                  ...activeNavbar,
                                });
                              }}
                              className={({ isActive }) =>
                                `submenu-item submenu-button ${sub.path && isActive ? "active" : ""}`
                              }
                            >
                              {sub.icon && (
                                <img
                                  src={sub.icon}
                                  alt={sub.label}
                                  className="submenu-icon"
                                />
                              )}
                              {sub.label}
                            </NavLink>
                          </NavItem>
                        )}
                      </>
                    ))}
                  </Nav>
                ) : (
                  <p className="no-options">No options</p>
                )}
              </div>
              <DisplayVersion showRelease />
            </div>
          </Box>
        )}

        <LogoutConfirmationPopup
          isOpen={isLogoutPopupOpen}
          onClose={() => setIsLogoutPopupOpen(false)}
          handleLogOut={handleLogout}
          label="Logout"
        />
        <div className="profile-sidebar-container">
          {isProfileSidebarOpen && !isSuperParter ? (
            <ProfileSidebar
              isOpen={isProfileSidebarOpen}
              onClose={() => setIsProfileSidebarOpen(false)}
              onProfileClick={handleProfileClick}
            />
          ) : (
            <></>
          )}
        </div>

        {alertMessage?.open && (
          <AlertComponent
            message={alertMessage?.message}
            type="success"
            open={alertMessage?.open}
            onClose={() => setAlertMessage({ open: false })}
          />
        )}
      </div>
    </>
  );
};

export default Sidebar;
